import { NSARouterMakerResult } from "namirasoft-account-react";
import { NamingConvention } from "namirasoft-core";
import { TokenAuthRow } from "namirasoft-credential";
import { ChatInferencerType, InferencerType, LLMItemRow, ModelInputRow, ModelModelCompany, ModelModelName, ModelRow } from "namirasoft-inference";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBox, NSBoxCombo, NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSSpace, NSSpaceSizeType } from "namirasoft-site-react";
import { Component, createRef, Fragment } from "react";
import ChatList from "../components/ChatList";
import InferencerCommonFields from "../components/InferencerCommonFields";
import InferencerGraph from "../components/InferencerGraph";
import InferencerLoader from "../components/InferencerLoader";
import InferencerVisualizer from "../components/InferencerVisualizer";
import UsageChart from "../components/UsageChart";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";
import { SquirrelProviderType } from "../SquirrelProviderType";
import { SquirrelResultFormatType } from "../SquirrelResultFormatType";

interface ModelEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

interface ModelEditPageState
{
    selected_model_company?: ModelModelCompany;
    selected_model_name?: ModelModelName;
    llm_models: LLMItemRow[];
    pending_row?: ModelRow;
}

export default class ModelEditPage extends Component<ModelEditPageProps, ModelEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEnum_ModelCompany = createRef<NSBoxEnum<ModelModelCompany>>();
    NSBoxCombo_ModelName = createRef<NSBoxCombo>();
    NSBoxEntity_ModelApiKeyCredentialID = createRef<NSBoxEntity<TokenAuthRow>>();
    NSBoxEnum_SquirrelProviders = createRef<NSBoxEnum<SquirrelProviderType>>();
    NSBoxEnum_SquirrelResultFormat = createRef<NSBoxEnum<SquirrelResultFormatType>>();
    InferencerCommonFields_Ids = createRef<InferencerCommonFields>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: ModelEditPageProps)
    {
        super(props);
        this.state = { llm_models: [] };
    }

    override componentDidMount()
    {
        this.props.servers.inference.llm.List().then((res) =>
        {
            this.setState({ llm_models: res.rows }, () =>
            {
                this.NSBoxEnum_ModelCompany.current?.reload();
                this.NSBoxCombo_ModelName.current?.reload();
                let pending = this.state.pending_row;
                if (pending)
                {
                    this.setState({ pending_row: undefined }, () =>
                    {
                        this.applyModelSelection(pending);
                    });
                }
            });
        }).catch(() => { });
    }

    private applyModelSelection(row: ModelRow)
    {
        this.NSBoxEnum_ModelCompany.current?.setValue(row.model_company, () =>
        {
            this.setState({
                selected_model_company: row.model_company,
                selected_model_name: row.model_name,
            }, () =>
            {
                this.NSBoxCombo_ModelName.current?.setValue(row.model_name);
                let providers = Array.isArray(row.settings?.["providers"]) ? row.settings!["providers"] as string[] : [];
                this.NSBoxEnum_SquirrelProviders.current?.setValueAll(providers);
                let result_format = typeof row.settings?.["result_format"] === "string" ? row.settings!["result_format"] as string : SquirrelResultFormatType.Markdown;
                this.NSBoxEnum_SquirrelResultFormat.current?.setValue(result_format);
            });
        });
    }

    private buildSettings(model_company: ModelModelCompany): Record<string, any> | null
    {
        if (model_company !== ModelModelCompany.Namirasoft)
            return null;
        let providers = this.NSBoxEnum_SquirrelProviders.current?.getValueEnumAll(SquirrelProviderType, SquirrelProviderType.ChatGPT) ?? [];
        let result_format = this.NSBoxEnum_SquirrelResultFormat.current?.getValueEnumOne(SquirrelResultFormatType, SquirrelResultFormatType.Markdown) ?? SquirrelResultFormatType.Markdown;
        let settings: Record<string, any> = { result_format };
        if (providers.length > 0)
            settings["providers"] = providers;
        return settings;
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        return (
            <InferenceLayout<ModelRow, ModelInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.model.edit : InferenceInfo.model.create }}
                pages={{
                    entity: InferenceEntities.model(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_view: {
                                tabs: {
                                    extra: {
                                        after_general: [
                                            {
                                                title: "Chats",
                                                clicked: false,
                                                isVisible: (model) => !!model.id,
                                                getContent: (model) => model.id
                                                    ? <ChatList
                                                        {...this.props}
                                                        inferencer_type={ChatInferencerType.MODEL}
                                                        inferencer_id={model.id}
                                                    />
                                                    : null,
                                            },
                                            {
                                                title: "Usage",
                                                clicked: false,
                                                isVisible: (model) => !!model.id,
                                                getContent: (model) => model.id
                                                    ? <UsageChart
                                                        {...this.props}
                                                        inferencer_type={InferencerType.MODEL}
                                                        inferencer_id={model.id}
                                                    />
                                                    : null,
                                            },
                                        ],
                                    },
                                },
                            },
                            tab_page_edit: {
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Common",
                                            clicked: true,
                                            getContent: () => (
                                                <InferencerCommonFields ref={this.InferencerCommonFields_Ids} {...this.props} info_entity="model" />
                                            ),
                                        },
                                    ],
                                },
                                editor: {
                                    getInputRow: (): ModelInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let model_company = (this.NSBoxEnum_ModelCompany.current?.getValueOne() as ModelModelCompany | null | undefined) ?? ModelModelCompany.OpenAI;
                                        let model_name = (this.NSBoxCombo_ModelName.current?.getValueOne() ?? "") as ModelModelName;
                                        let model_api_key_credential_id = this.NSBoxEntity_ModelApiKeyCredentialID.current?.getValueOne() ?? null;
                                        let { validator_id, budget_id, memory_id, wiper_id, log_group_id } = this.InferencerCommonFields_Ids.current?.getValue() ?? InferencerCommonFields.EMPTY;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;
                                        let settings = this.buildSettings(model_company);

                                        return {
                                            workspace_id: null,
                                            name,
                                            model_company,
                                            model_name,
                                            settings,
                                            model_api_key_credential_id,
                                            validator_id,
                                            budget_id,
                                            memory_id,
                                            wiper_id,
                                            log_group_id,
                                            description,
                                            model_category: [],
                                            model_field: [],
                                            model_tag: [],
                                        };
                                    },
                                    setRow: (row: ModelRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEntity_ModelApiKeyCredentialID.current?.setValue(row.model_api_key_credential_id);
                                        this.InferencerCommonFields_Ids.current?.setValue(row);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);
                                        if (this.state.llm_models.length === 0)
                                            this.setState({ pending_row: row });
                                        else
                                            this.applyModelSelection(row);
                                    }
                                },
                            },
                            getChildrenAfter: (id, _row) => id
                                ? <Fragment>
                                    <NSSpace size={NSSpaceSizeType.SMALL} />
                                    <InferencerLoader
                                        {...this.props}
                                        inferencer_type={InferencerType.MODEL}
                                        inferencer_id={id}
                                        render={({ tree, loading, error }) => (
                                            <Fragment>
                                                <InferencerGraph tree={tree} loading={loading} error={error} />
                                                <NSSpace size={NSSpaceSizeType.SMALL} />
                                                <InferencerVisualizer tree={tree} loading={loading} error={error} />
                                            </Fragment>
                                        )}
                                    />
                                </Fragment>
                                : null,
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        required
                        title="Name"
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/model/#field-name" }}
                    />
                    <NSBoxEnum<ModelModelCompany>
                        ref={this.NSBoxEnum_ModelCompany}
                        title="Model Company"
                        required
                        multiple={false}
                        info={{ link: "https://namirasoft.com/inference/console-guide/model/#field-model-company" }}
                        getEnumObject={() =>
                        {
                            let companies = Array.from(new Set(this.state.llm_models.map(m => m.company as unknown as string))).sort();
                            let obj: { [s: string]: ModelModelCompany } = {};
                            for (let c of companies)
                                obj[c] = c as unknown as ModelModelCompany;
                            return obj;
                        }}
                        onChanged={(box) =>
                        {
                            let company = (box.getValueOne(false) ?? undefined) as ModelModelCompany | undefined;
                            this.NSBoxCombo_ModelName.current?.setValue(null);
                            this.NSBoxCombo_ModelName.current?.reload();
                            if (company === ModelModelCompany.Namirasoft)
                                this.NSBoxEntity_ModelApiKeyCredentialID.current?.setValue(null);
                            this.setState({ selected_model_company: company, selected_model_name: undefined }, () =>
                            {
                                if (company === ModelModelCompany.Namirasoft && !this.NSBoxEnum_SquirrelResultFormat.current?.getValueOne(false))
                                    this.NSBoxEnum_SquirrelResultFormat.current?.setValue(SquirrelResultFormatType.Markdown);
                            });
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    {
                        this.state.selected_model_company &&
                        <NSBoxCombo
                            ref={this.NSBoxCombo_ModelName}
                            title="Model Name"
                            required
                            multiple={false}
                            info={{ link: "https://namirasoft.com/inference/console-guide/model/#field-model-name" }}
                            getOptions={() =>
                            {
                                let company = (this.NSBoxEnum_ModelCompany.current?.getValueOne(false) as ModelModelCompany | null | undefined) ?? this.state.selected_model_company;
                                if (!company)
                                    return [];
                                return this.state.llm_models
                                    .filter(m => (m.company as unknown as string) === (company as unknown as string))
                                    .map(m => ({
                                        value: m.name,
                                        title: NamingConvention.auto.convert(m.name, NamingConvention.Pascal_Case_Space),
                                    }))
                                    .sort((a, b) => a.title.localeCompare(b.title));
                            }}
                            onChanged={(box) =>
                            {
                                this.setState({ selected_model_name: (box.getValueOne(false) ?? undefined) as ModelModelName | undefined });
                            }}
                            style={{ width: NSBox.width.double }}
                        />
                    }
                    {
                        this.state.selected_model_company && this.state.selected_model_company !== ModelModelCompany.Namirasoft && (
                            <NSBoxEntity<TokenAuthRow>
                                ref={this.NSBoxEntity_ModelApiKeyCredentialID}
                                {...this.props}
                                title="Model API Key Credential ID"
                                required={true}
                                multiple={false}
                                info={{ link: "https://namirasoft.com/inference/console-guide/model/#field-model-api-key-credential" }}
                                table_name="model"
                                table={namirasoft.credential.tables.token_auth}
                                getValue={(item) => item.id}
                                getTitle={(item) => item.name}
                                getSort={() => null}
                                style={{ width: NSBox.width.double }}
                            />
                        )}
                    {
                        this.state.selected_model_company === ModelModelCompany.Namirasoft && (
                            <NSBoxEnum<SquirrelProviderType>
                                ref={this.NSBoxEnum_SquirrelProviders}
                                title="Providers (empty = any)"
                                required={false}
                                multiple={true}
                                info={{ link: "https://namirasoft.com/inference/console-guide/model/#field-providers" }}
                                getEnumObject={() => SquirrelProviderType}
                                style={{ width: NSBox.width.double }}
                            />
                        )}
                    {
                        this.state.selected_model_company === ModelModelCompany.Namirasoft && (
                            <NSBoxEnum<SquirrelResultFormatType>
                                ref={this.NSBoxEnum_SquirrelResultFormat}
                                title="Result Format"
                                required={true}
                                multiple={false}
                                info={{ link: "https://namirasoft.com/inference/console-guide/model/#field-result-format" }}
                                getEnumObject={() => SquirrelResultFormatType}
                                style={{ width: NSBox.width.double }}
                            />
                        )}
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/model/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}